import { useState } from "react";
import { Lock, ShieldCheck, Users, Wrench } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { FButton } from "@/components/common/FButton";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "border-white/15 bg-ff-midnight text-ff-ivory placeholder:text-ff-grey/50 focus-visible:ring-ff-ember focus-visible:ring-offset-0";

const portals = [
  { key: "creator", label: "Creator", icon: ShieldCheck, blurb: "Your workspace, content tools and cashout requests." },
  { key: "ambassador", label: "Ambassador", icon: Users, blurb: "Your referral code, tracking and rewards." },
  { key: "admin", label: "Admin", icon: Wrench, blurb: "Operations center for review and approvals." },
];

/* NOTE: Auth is intentionally a placeholder for MVP.
   Codex will connect Supabase Auth later (email/OTP or provider).
   These forms do not authenticate — they show a "coming soon" state. */
function LoginPanel({ portal }) {
  const [email, setEmail] = useState("");
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <form
        onSubmit={(e) => { e.preventDefault(); toast.info(`${portal.label} portal is coming soon.`); }}
        className="space-y-5 rounded-3xl border border-white/10 bg-ff-slate/60 p-8"
        data-testid={`login-form-${portal.key}`}
      >
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ff-ember/15 text-ff-ember">
            <portal.icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-xl font-semibold text-ff-ivory">{portal.label} Login</h3>
            <p className="text-xs text-ff-grey">Placeholder — no account required yet</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`login-email-${portal.key}`} className="text-sm font-medium text-ff-grey">Email</Label>
          <Input
            id={`login-email-${portal.key}`}
            type="email"
            data-testid={`login-email-${portal.key}`}
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium text-ff-grey">Password</Label>
          <Input type="password" disabled className={`${inputCls} opacity-60`} placeholder="Connected with Supabase Auth soon" />
        </div>

        <FButton type="submit" data-testid={`login-submit-${portal.key}`} className="w-full">
          Continue
        </FButton>
        <p className="text-center text-xs text-ff-grey">
          Don’t have access yet?{" "}
          <a href="/apply" className="text-ff-ember hover:text-ff-ivory">Apply to FanFuel</a>
        </p>
      </form>

      {/* locked portal preview */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-ff-slate/40 p-8" data-testid={`portal-locked-${portal.key}`}>
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-ff-midnight/70 backdrop-blur-sm">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/5 text-ff-grey">
            <Lock className="h-6 w-6" />
          </span>
          <StatusBadge tone="warning">Coming soon</StatusBadge>
          <p className="max-w-xs text-center text-sm text-ff-grey">
            {portal.label} Portal is coming soon. {portal.blurb}
          </p>
        </div>
        {/* faux dashboard behind blur */}
        <div className="space-y-4 opacity-40" aria-hidden="true">
          <div className="h-8 w-40 rounded-lg bg-white/10" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-24 rounded-xl bg-white/5" />
            <div className="h-24 rounded-xl bg-white/5" />
          </div>
          <div className="h-40 rounded-xl bg-white/5" />
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  return (
    <div data-testid="login-page">
      <PageHeader
        overline="Login"
        titleLines={["Welcome back."]}
        lead="Portals are being built. For now, choose a portal to preview — and apply if you don't have access yet."
        testid="login-header"
      />

      <section className="px-5 pb-20 md:px-8 md:pb-28">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <Tabs defaultValue="creator" className="w-full">
              <TabsList className="grid w-full grid-cols-3 rounded-full border border-white/10 bg-ff-slate/60 p-1" data-testid="login-tabs">
                {portals.map((p) => (
                  <TabsTrigger
                    key={p.key}
                    value={p.key}
                    data-testid={`login-tab-${p.key}`}
                    className="rounded-full text-xs font-semibold text-ff-grey data-[state=active]:bg-ff-ember data-[state=active]:text-ff-midnight sm:text-sm"
                  >
                    {p.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {portals.map((p) => (
                <TabsContent key={p.key} value={p.key} className="mt-10">
                  <LoginPanel portal={p} />
                </TabsContent>
              ))}
            </Tabs>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
